import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "input", "results", "hidden"]
  static values = {
    url: String,
    field: String,
    model: String,
    searchUrl: String,
    currentLabel: String,
    allowNull: { type: Boolean, default: true }
  }

  connect() {
    this.editing = false
    this.debounceTimer = null
  }

  edit(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.editing) return
    this.editing = true
    this.displayTarget.classList.add("hidden")
    this.inputTarget.classList.remove("hidden")
    this.resultsTarget.classList.remove("hidden")
    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.search()
  }

  input() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.search(), 180)
  }

  async search() {
    const query = this.inputTarget.value.trim()
    // Séparateur dynamique : si l'URL contient déjà un query string (ex: ?scope=b2b),
    // on concatène avec "&", sinon "?". Sans ça, `/search/users.json?scope=b2b?q=martin`
    // est malformé et Rails ignore le param `q` (ou le scope selon parsing).
    const sep = this.searchUrlValue.includes("?") ? "&" : "?"
    const url = `${this.searchUrlValue}${sep}q=${encodeURIComponent(query)}`

    try {
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      if (!res.ok) return
      const data = await res.json()
      this.renderResults(Array.isArray(data) ? data : [])
    } catch (_e) {
      // swallow network errors silently
    }
  }

  renderResults(items) {
    this.resultsTarget.innerHTML = ""

    if (this.allowNullValue) {
      const clearBtn = this.buildItem({ id: "", name: "— Aucun" }, "rw-inline-combobox-item--clear")
      this.resultsTarget.appendChild(clearBtn)
    }

    if (items.length === 0) {
      const empty = document.createElement("div")
      empty.className = "rw-inline-combobox-empty"
      empty.textContent = "Aucun résultat"
      this.resultsTarget.appendChild(empty)
      return
    }

    items.forEach(item => {
      this.resultsTarget.appendChild(this.buildItem(item))
    })
  }

  buildItem(item, extraClass = "") {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = `rw-inline-combobox-item ${extraClass}`.trim()
    btn.dataset.id = item.id
    btn.dataset.label = item.name || item.label || ""

    const label = document.createElement("span")
    label.className = "rw-inline-combobox-item-label"
    label.textContent = item.name || item.label || "—"
    btn.appendChild(label)

    // Ville, code postal, SIRET d'un compte : sous le nom, la colonne de droite est trop étroite
    if (item.hint) {
      const hint = document.createElement("span")
      hint.className = "block text-xs text-text-muted truncate"
      hint.textContent = item.hint
      label.appendChild(hint)
    }

    if (item.role) {
      const meta = document.createElement("span")
      meta.className = "rw-inline-combobox-item-meta"
      meta.textContent = item.role
      btn.appendChild(meta)
    }

    btn.addEventListener("mousedown", (e) => {
      e.preventDefault()
      this.select(item)
    })

    return btn
  }

  select(item) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const value = item.id === "" ? null : item.id
    const body = { [this.modelValue]: { [this.fieldValue]: value } }

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify(body)
    }).then(response => {
      if (response.ok) {
        const textNode = this.displayTarget.querySelector(".rw-inline-display-text") || this.displayTarget
        const fieldWrap = this.element.closest(".rw-inline-field")?.querySelector(".rw-inline-field-value")

        if (!value) {
          textNode.textContent = "— Ajouter"
          fieldWrap?.classList.add("is-empty")
        } else {
          textNode.textContent = item.name || item.label || "—"
          fieldWrap?.classList.remove("is-empty")
        }

        this.close()
        this.flashSuccess()
      }
    })
  }

  close() {
    this.editing = false
    this.displayTarget.classList.remove("hidden")
    this.inputTarget.classList.add("hidden")
    this.resultsTarget.classList.add("hidden")
  }

  handleBlur() {
    setTimeout(() => {
      if (document.activeElement !== this.inputTarget && !this.resultsTarget.contains(document.activeElement)) {
        this.close()
      }
    }, 200)
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    }
  }

  flashSuccess() {
    const field = this.element.closest(".rw-inline-field")
    if (!field) return
    field.classList.remove("is-just-saved")
    void field.offsetWidth
    field.classList.add("is-just-saved")
    setTimeout(() => field.classList.remove("is-just-saved"), 1200)
  }
}
